import { Box, IconButton } from '@mui/material';
import {
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
} from '@mui/x-data-grid-pro';

import { IcDotSpread } from '@/assets/images';
import { excludeValues, formatDate } from '@/components/DataGrid/utils';
import {
  IOffenseOption,
  IOffenseOptionItem,
  IProperty,
  PropertyStatus,
  PropertyStatusList,
} from '@/models';
import {
  getOffenseOptionItems,
  returnCleanValue,
} from '@/pages/Kases/KaseCrimes/utilities';
import { formatPersonName, getMasterNameDisplayName } from '@/utils';

const getPropertyCurrentLocation = (row: IProperty): string => {
  const custodyInfo = row.custodyActionResultInfo;
  if (!custodyInfo) return '';

  const inventoryLocationName =
    'inventoryLocation' in custodyInfo
      ? custodyInfo.inventoryLocation?.name || ''
      : '';
  const vendorName =
    'vendor' in custodyInfo ? custodyInfo.vendor?.name || '' : '';
  const agencyName =
    'agency' in custodyInfo ? custodyInfo.agency?.name || '' : '';

  const masterNameValue =
    'masterName' in custodyInfo
      ? getMasterNameDisplayName(custodyInfo.masterName)
      : '';
  const userFullName =
    'user' in custodyInfo ? formatPersonName(custodyInfo.user) : '';
  const userEmail = 'user' in custodyInfo ? custodyInfo.user?.email || '' : '';

  switch (row.status) {
    case PropertyStatus.IN_POLICE_CUSTODY:
      return inventoryLocationName;
    case PropertyStatus.RELEASED_TO_COURT:
    case PropertyStatus.RELEASED_TO_LAB:
      return vendorName;
    case PropertyStatus.RELEASED_TO_PERSON:
      return masterNameValue || userFullName || userEmail;
    case PropertyStatus.TRANSFERRED:
      return agencyName;
    default:
      return inventoryLocationName;
  }
};

const number = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
  // For CFS-synced (mirrored) properties, display the source CFS property's number
  valueGetter: (_, row) => row.sourceCfsProperty?.number || row.number,
});

const name = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const typeTag = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const itemNumber = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const dateLogged = (): Partial<GridColDef> => ({
  filterOperators: getGridDateOperators(true),
  renderCell: (params) => formatDate(params.row.loggedInfo?.loggedDate),
  valueGetter: (_, row) => row.loggedInfo?.loggedDate,
});

const loggedBy = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const tempLoggedBy = params.row.loggedInfo?.loggedBy;
    return tempLoggedBy ? tempLoggedBy.fullName : '';
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues(['equals', 'doesNotEqual', 'isAnyOf']),
  ),
  valueGetter: (_, row) => row.loggedInfo?.loggedBy?.fullName,
});

const status = (): Partial<GridColDef> => ({
  renderCell: (params) => PropertyStatusList.get(params.row.status),
  filterable: false,
});

const loggedAddress = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(excludeValues(['isAnyOf'])),
  headerName: 'Location',
  renderCell: (params) => getPropertyCurrentLocation(params.row),
  valueGetter: (_, row) => getPropertyCurrentLocation(row),
});

const actions = (
  openMenu?: (
    event: React.MouseEvent<HTMLButtonElement>,
    params: IProperty,
  ) => void,
): Partial<GridColDef> => ({
  sortable: false,
  filterable: false,
  renderCell: (params) => (
    <Box>
      <IconButton onClick={(e) => openMenu?.(e, params.row)}>
        <IcDotSpread />
      </IconButton>
    </Box>
  ),
});

export const kasePropertyCols = (
  openMenu?: (
    event: React.MouseEvent<HTMLButtonElement>,
    params: IProperty,
  ) => void,
  filteredOptions?: IOffenseOption[],
  agencyPropertyTypes?: { value: string; label: string }[],
) => {
  const type = (): Partial<GridColDef> => {
    const PropertyType = getOffenseOptionItems(
      'propertyType',
      filteredOptions || [],
    ) as Map<string, IOffenseOptionItem>;

    const resolveLabel = (propertyType: string) => {
      if (!propertyType) return '';
      const foundItem = Array.from(PropertyType.values()).find(
        (val) => val.key == propertyType,
      );
      if (foundItem?.label) return foundItem.label;
      const agencyItem = agencyPropertyTypes?.find(
        (item) => item.value === propertyType,
      );
      return agencyItem?.label || propertyType || '';
    };

    return {
      renderCell: (params) => resolveLabel(params.row.type),
      filterable: false,
      valueGetter: (_, row) => resolveLabel(row.type),
    };
  };

  const involvement = (): Partial<GridColDef> => {
    if (!filteredOptions || filteredOptions.length === 0) {
      return {};
    }
    const PropertyInvolvementList = getOffenseOptionItems(
      'involvement',
      filteredOptions,
    ) as Map<string, IOffenseOptionItem>;
    return {
      renderCell: (params) => {
        return returnCleanValue(
          PropertyInvolvementList?.get(params.row.involvement)?.label,
        );
      },
      filterable: false,
      valueGetter: (_, row) => {
        return returnCleanValue(
          PropertyInvolvementList?.get(row.involvement)?.label,
        );
      },
    };
  };

  return {
    number,
    name,
    typeTag,
    itemNumber,
    type,
    involvement,
    dateLogged,
    loggedBy,
    status,
    loggedAddress,
    actions: () => actions(openMenu),
  };
};
